import { useContext, useMemo, useCallback, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { t } from '@lingui/core/macro'
import CircularProgress from '@mui/material/CircularProgress'
import { object } from 'prop-types'
import { useSelector } from 'react-redux'
import {
  CvsAutoComplete,
  CvsTextField,
  CvsFieldMapper,
  InboundObjectValidator,
  CvsFieldMapperV2,
} from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { advertiserContext, placementContext, campaignContext, connectorContext } from '@contexts'
import { api } from 'api'
import { templateCopyContext } from '../context'
import {
  getMappableFields,
  transformIntegrationFieldMappings,
} from '../../../../../components/field-mapperV2/utils'

const FullWidth = styled.div`
  width: 100%;
`

const FullWidthTextField = styled(CvsTextField)`
  width: 100%;
`

const dcmFields = [
  'campaignId',
  'creativeId',
  'placementId',
  'clickThroughUrl',
  'siteId',
  'siteKeyname',
  'placementName',
  'contentCategoryId',
  'contentCategory',
  'placementStrategy',
  'placementStartDate',
  'placementEndDate',
  'placementGroupType',
  'placementCostStructure',
  'placementCapCostOption',
  'placementFlightingActivated',
  'placementActivtyId',
  'advertiserId',
  'adId',
  'adName',
  'adType',
  'creativeName',
  'creativePixelSize',
  'comments',
  'campaign',
  'campaignStartDate',
  'campaignEndDate',
  'billingInvoiceCode',
  'siteName',
  'generated_link',
]

const DCMDefaultMappingFields = [
  {
    cvFieldId: '',
    required: true,
    fieldName: 'adId',
    type: 'integration_field',
  },
  {
    cvFieldId: '',
    required: true,
    fieldName: 'campaign',
    type: 'integration_field',
  },
  {
    cvFieldId: '',
    required: true,
    fieldName: 'campaignId',
    type: 'integration_field',
  },
  {
    cvFieldId: '',
    required: true,
    fieldName: 'placementId',
    type: 'integration_field',
  },
]

export const GoogleDCM = ({ assignment }) => {
  const frequency = {
    daily: t`Daily`,
    'twice-daily': t`Twice Daily`,
    weekly: t`Weekly`,
    'manual-only': t`Manual Only`,
  }

  const { template, onChange } = useContext(templateCopyContext)
  const { byId } = useContext(connectorContext)
  const { fetchAdvertisers, byId: adsById } = useContext(advertiserContext)
  const { fetchCampaigns, byId: campById } = useContext(campaignContext)
  const { fetchPlacements, byId: placeById } = useContext(placementContext)
  const [loadingFields, setLoadingFields] = useState(true)
  const [fields, setFields] = useState([])

  const gcmNewMapping = useSelector(state => state.Auth.features['airbyte-for-gcm-integration'])

  useEffect(() => {
    if (gcmNewMapping) {
      convertGoogleDCMToV2()
      api
        .get(`/api/v1/integrations/google-dcm/default-fields`)
        .then(resp => {
          setFields(resp.data)
          setLoadingFields(false)
        })
        .catch(_err => {
          setLoadingFields(false)
        })
    }
  }, [])

  useEffect(() => {
    if (assignment.connector_id) {
      fetchAdvertisers(assignment.connector_id)
      fetchCampaigns(assignment.connector_id)
      fetchPlacements(assignment.connector_id)
    }
  }, [assignment.connector_id])

  const fieldOptions = useMemo(
    () =>
      Object.values(template.templateFieldsById).map(field => ({
        label: field.field?.name,
        value: field.field?.id?.toString(),
      })),
    // .filter(({ value }) => {
    //   return !Object.values(assignment.metadata).includes(value)
    // }),
    [template],
  )

  const connectors = useMemo(
    () =>
      Object.values(byId)
        .filter(
          connector =>
            connector.connector_type_id ===
            (assignment.connector_type_id
              ? assignment.connector_type_id
              : byId[assignment.connector_id].connector_type_id),
        )
        .map(({ name, id }) => ({
          label: name,
          value: id,
        })),
    [assignment, byId],
  )

  const convertGoogleDCMToV2 = () => {
    onChange(old => ({
      ...old,
      [assignment.id]: {
        ...old[assignment.id],
        mapping_fields: DCMDefaultMappingFields,
        new_mapping: true,
      },
    }))
  }

  const onGoogleDCMChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          [name]: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const onGoogleDCMMetadataChange = useCallback(
    (value, name) => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          metadata: {
            ...old[assignment.id].metadata,
            [name]: value,
          },
        },
      }))
    },
    [onChange, assignment],
  )

  const onGoogleDCMMappingChange = useCallback(
    value => {
      onChange(old => ({
        ...old,
        [assignment.id]: {
          ...old[assignment.id],
          mapping_fields: value,
        },
      }))
    },
    [assignment, onChange],
  )

  const replaceImportObjectFilters = useCallback(
    (value, key) => {
      // TODO: should never work with JSON in forms. Move this to the save transform.
      const obj = assignment.metadata.importObjectFilters
        ? JSON.parse(assignment.metadata.importObjectFilters)
        : {}
      onGoogleDCMMetadataChange(JSON.stringify({ ...obj, [key]: value }), 'importObjectFilters')
    },
    [template, assignment, onGoogleDCMMetadataChange],
  )

  const mappingValues = useMemo(() => {
    const { metadata } = assignment
    const required = {
      adId: '',
      campaignId: '',
      placementId: '',
      campaign: '',
    }

    const values = Object.keys(metadata)
      .filter(
        opt => opt !== 'importFilters' && opt !== 'importObjectFilters' && opt !== 'syncFrequency',
      )
      .reduce((acc, key) => ({ ...acc, [key]: metadata[key] }), {})

    const retunval = { ...required, ...values }
    return retunval
  }, [template, assignment])

  const advertiserOptions = (adsById[assignment.connector_id] || []).map(a => ({
    label: `${a.name} (${a.id})`,
    value: a.id,
  }))
  const campaignOptions = (campById[assignment.connector_id] || []).map(a => ({
    label: `${a.name} (${a.id})`,
    value: a.id,
  }))
  const placementOptions = (placeById[assignment.connector_id] || []).map(a => ({
    label: `${a.name} (${a.id})`,
    value: a.id,
  }))

  const filterOptions = [
    { label: t`Include Archived Campaigns`, value: 'includeArchivedCampaigns' },
    { label: t`Include Past End Date Campaigns`, value: 'includePastEndDateCampaigns' },
    { label: t`Include Ads Missing Creatives`, value: 'includeAdsMissingCreatives' },
    { label: t`Include Inactive Ads`, value: 'includeInactiveAds' },
    { label: t`Include Ads Missing Placements`, value: 'includeAdsMissingPlacements' },
    { label: t`Exclude Inactive Creatives`, value: 'excludeInactiveCreatives' },
    { label: t`Exclude Archived Placements`, value: 'excludeArchivedPlacements' },
    { label: t`Exclude Archived Creatives`, value: 'excludeArchivedCreatives' },
    { label: t`Exclude Archived Ads`, value: 'excludeArchivedAds' },
  ]

  return (
    <FullWidth>
      <FullWidthTextField
        label={t`Integration name`}
        onChange={e => onGoogleDCMChange(e.target.value, 'name')}
        value={assignment.name}
      />
      <CvsAutoComplete
        options={connectors}
        onSelected={selected => onGoogleDCMChange(selected, 'connector_id')}
        selected={assignment.connector_id}
        label={t`Account`}
      />
      <CvsAutoComplete
        options={filterOptions}
        onSelected={values => onGoogleDCMMetadataChange(JSON.stringify(values), 'importFilters')}
        selected={formatSelectedOptions(
          true,
          JSON.parse(assignment.metadata.importFilters || '[]'),
          filterOptions,
        )}
        label={t`Import filters`}
        isMulti
      />
      <CvsAutoComplete
        options={advertiserOptions}
        onSelected={selected => replaceImportObjectFilters(selected, 'advertisers')}
        selected={formatSelectedOptions(
          true,
          JSON.parse(assignment.metadata.importObjectFilters || '{}').advertisers || [],
          advertiserOptions,
        )}
        label={t`Limit Import By ADVERTISERS`}
        isMulti
      />
      <CvsAutoComplete
        options={campaignOptions}
        onSelected={selected => replaceImportObjectFilters(selected, 'campaigns')}
        selected={formatSelectedOptions(
          true,
          JSON.parse(assignment.metadata.importObjectFilters || '{}').campaigns || [],
          campaignOptions,
        )}
        label={t`Limit Import By CAMPAIGNS`}
        isMulti
      />
      <CvsAutoComplete
        options={placementOptions}
        onSelected={selected => replaceImportObjectFilters(selected, 'placements')}
        selected={formatSelectedOptions(
          true,
          JSON.parse(assignment.metadata.importObjectFilters || '{}').placements || [],
          placementOptions,
        )}
        label={t`Limit Import By PLACEMENTS`}
        isMulti
      />
      <CvsAutoComplete
        options={Object.keys(frequency).map(key => ({ value: key, label: frequency[key] }))}
        onSelected={selected => onGoogleDCMMetadataChange(selected, 'syncFrequency')}
        selected={assignment.metadata.syncFrequency}
        label={t`Data sync frequency`}
      />
      <InboundObjectValidator
        connector_id={assignment.connector_id}
        objectOptions={['campaigns', 'ads', 'placements', 'creatives', 'sites', 'advertisers']}
      />
      {gcmNewMapping ? (
        <div>
          {loadingFields ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
              <CircularProgress color="primary" />
            </div>
          ) : (
            <CvsFieldMapperV2
              mappingType="inbound"
              integrationTitle={t`Google DCM`}
              integrationOptions={getMappableFields(fields, [])}
              integration={assignment}
              templateOptions={fieldOptions}
              mappings={transformIntegrationFieldMappings(assignment.mapping_fields)}
              onSelect={value => onGoogleDCMMappingChange(value)}
            />
          )}
        </div>
      ) : (
        <CvsFieldMapper
          fromOptions={dcmFields.map(o => ({ label: o, value: o }))}
          toOptions={fieldOptions}
          requiredMappings={['adId', 'campaignId', 'placementId', 'campaign']}
          value={mappingValues}
          onSelect={value => {
            const { importFilters, importObjectFilters, syncFrequency, ..._rest } =
              assignment.metadata
            void _rest //eslint bug
            const replacement = { importFilters, importObjectFilters, syncFrequency }
            const mappings = Object.keys(value).reduce(
              (acc, key) => ({ ...acc, [key]: value[key] }),
              {},
            )
            onChange(old => ({
              ...old,
              [assignment.id]: {
                ...old[assignment.id],
                metadata: {
                  ...replacement,
                  ...mappings,
                },
              },
            }))
          }}
        />
      )}
    </FullWidth>
  )
}

GoogleDCM.propTypes = {
  assignment: object.isRequired,
}
